/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Mortgage application — /real-estate/mortgage-applications/:id
 * GET /re/mortgage-applications/{id} (embeds stage_history, lender, applicant names),
 * PUT (via MortgageForm), POST /stage {to_stage, notes}.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Link, MenuItem, Skeleton,
  TextField,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import InfoCard from './components/ReInfoCard';
import { usePermission } from 'auth/usePermission';
import RePage, { LoadError, useSnack } from './components/RePage';
import ReStatusChip from './components/ReStatusChip';
import ReStepper from './components/ReStepper';
import { KV } from './components/Widgets';
import MortgageForm, { MortgageMetrics, WARNING_TEXT } from './components/MortgageForm';
import { TONES, toneFor } from './components/statusColors';
import {
  MORTGAGE_STAGES, errorMessage, formatDate, formatDateTime, formatMoney, formatPct, humanize, useReApi, useReGet,
} from './api';
import { buyerCategoryOptions, defaultCurrency, isAustralia } from '../variant';

const FLOW = MORTGAGE_STAGES.filter((s) => !['DECLINED', 'WITHDRAWN'].includes(s));

function StageDialog({ app, open, onClose, onDone }) {
  const { api } = useReApi();
  const idx = FLOW.indexOf(app?.stage);
  const [toStage, setToStage] = useState('');
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState(null);
  React.useEffect(() => {
    if (open) {
      setToStage(idx >= 0 && idx < FLOW.length - 1 ? FLOW[idx + 1] : '');
      setNotes('');
      setErr(null);
    }
  }, [open, idx]);
  const submit = async () => {
    if (!toStage) return;
    if (toStage === 'DECLINED' && !notes.trim()) {
      setErr('Record why the application was declined.');
      return;
    }
    try {
      await api.post(`/re/mortgage-applications/${app.id}/stage`, { to_stage: toStage, notes: notes || null });
      onDone(toStage);
    } catch (e) {
      setErr(errorMessage(e, 'Could not change the stage'));
    }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Change stage</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField select size="small" label="Move to" value={toStage} onChange={(e) => setToStage(e.target.value)}>
            {MORTGAGE_STAGES.filter((s) => s !== app?.stage).map((s) => <MenuItem key={s} value={s}>{humanize(s)}</MenuItem>)}
          </TextField>
          <TextField size="small" multiline minRows={2} label={toStage === 'DECLINED' ? 'Decline reason' : 'Notes'} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>Cancel</MDButton>
        <MDButton variant="gradient" color="info" onClick={submit} disabled={!toStage}>Move</MDButton>
      </DialogActions>
    </Dialog>
  );
}

export default function MortgageApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [snack, notify] = useSnack();
  const { canEdit } = usePermission('re_mortgage_applications');
  const { data: a, loading, error, reload, setData } = useReGet(`/re/mortgage-applications/${id}`);
  const [editOpen, setEditOpen] = useState(false);
  const [stageOpen, setStageOpen] = useState(false);

  const history = useMemo(
    () => [...(a?.stage_history || [])].sort((x, y) => new Date(y.changed_at) - new Date(x.changed_at)),
    [a],
  );

  if (loading && !a) return <RePage title="Mortgage application" onBack={() => navigate('/real-estate/mortgage-applications')}><Skeleton variant="rectangular" height={300} /></RePage>;
  if (!a) return <RePage title="Mortgage application" onBack={() => navigate('/real-estate/mortgage-applications')}><LoadError error={error} onRetry={reload} /></RePage>;

  const cur = a.currency || defaultCurrency();
  const flowIdx = FLOW.indexOf(a.stage);
  const closedOut = ['DECLINED', 'WITHDRAWN'].includes(a.stage);
  const warnings = a.warnings || [];
  const applicant = a.applicant_name || a.applicant_contact_name || `Contact #${a.applicant_contact_id}`;

  return (
    <RePage
      title={applicant}
      subtitle={[a.application_number, a.lender?.name || a.lender_name].filter(Boolean).join(' · ')}
      onBack={() => navigate('/real-estate/mortgage-applications')}
      actions={(
        <>
          <ReStatusChip vocab="mortgage" status={a.stage} />
          {canEdit && <MDButton size="small" variant="outlined" color="dark" onClick={() => setEditOpen(true)}>Edit</MDButton>}
          {canEdit && <MDButton size="small" variant="gradient" color="info" onClick={() => setStageOpen(true)}>Change stage</MDButton>}
        </>
      )}
    >
      <InfoCard icon="timeline" title="Progress">
        {closedOut ? (
          <Alert severity={a.stage === 'DECLINED' ? 'error' : 'info'}>
            {`${humanize(a.stage)}${a.decline_reason ? `: ${a.decline_reason}` : ''}`}
          </Alert>
        ) : (
          <ReStepper steps={FLOW.map(humanize)} activeStep={flowIdx} />
        )}
      </InfoCard>

      {warnings.length > 0 && (
        <MDBox mt={2}>
          {warnings.map((w) => <Alert key={w} severity="warning" sx={{ mb: 1 }}>{WARNING_TEXT[w] || humanize(w)}</Alert>)}
        </MDBox>
      )}

      <Grid container spacing={2} mt={0}>
        <Grid item xs={12} md={4}>
          <InfoCard icon="person" title="Applicant">
            <KV label="Applicant" value={<Link component="button" variant="button" onClick={() => navigate(`/contacts/${a.applicant_contact_id}`)}>{applicant}</Link>} />
            {a.co_applicant_contact_id && (
              <KV label="Co-applicant" value={<Link component="button" variant="button" onClick={() => navigate(`/contacts/${a.co_applicant_contact_id}`)}>{a.co_applicant_name || a.co_applicant_contact_name || `#${a.co_applicant_contact_id}`}</Link>} />
            )}
            {isAustralia()
              ? <KV label="Buyer" value={buyerCategoryOptions().find((o) => o.value === a.buyer_category)?.label || '—'} />
              : <KV label="Residency" value={humanize(a.residency_status) || '—'} />}
            <KV label="Employment" value={humanize(a.employment_type) || '—'} />
            <KV label="Monthly income" value={formatMoney(a.monthly_income, cur)} />
            <KV label="Existing liabilities" value={formatMoney(a.existing_liabilities, cur)} />
            <KV label="Advisor" value={a.assigned_to_name || '—'} />
          </InfoCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <InfoCard icon="account_balance" title="Loan">
            <KV label="Lender" value={a.lender?.name || a.lender_name || '—'} />
            <KV label="Bank reference" value={a.bank_reference || '—'} />
            <KV label="Property value" value={formatMoney(a.property_value, cur)} />
            <KV label="Loan amount" value={formatMoney(a.loan_amount, cur)} bold />
            <KV label="Tenure" value={a.tenure_months ? `${a.tenure_months} months (${Math.round(a.tenure_months / 12)} yrs)` : '—'} />
            <KV label="Rate" value={a.interest_rate_pct != null ? `${formatPct(a.interest_rate_pct, 2)} ${humanize(a.rate_type) || ''}` : '—'} />
            {isAustralia() ? (
              <>
                <KV label="LVR" value={formatPct(a.ltv_pct)} />
                <KV label="LMI" value={a.lmi_required == null ? '—' : (a.lmi_required ? 'Likely' : 'Not expected')} />
              </>
            ) : (
              <KV label="Stored LTV / DBR" value={`${formatPct(a.ltv_pct)} / ${formatPct(a.dbr_pct)}`} />
            )}
            {a.booking_id && <KV label="Booking" value={<Link component="button" variant="button" onClick={() => navigate(`/real-estate/bookings/${a.booking_id}`)}>{a.booking_number || `#${a.booking_id}`}</Link>} />}
            {a.unit_id && <KV label="Unit" value={<Link component="button" variant="button" onClick={() => navigate(`/real-estate/units/${a.unit_id}`)}>{a.unit_number || `#${a.unit_id}`}</Link>} />}
          </InfoCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <InfoCard icon="calculate" title="Affordability">
            <MortgageMetrics values={a} lender={a.lender} />
            <MDBox mt={2}>
              <KV label="Pre-approval" value={a.pre_approval_date ? `${formatDate(a.pre_approval_date)} (expires ${formatDate(a.pre_approval_expiry)})` : '—'} />
              <KV label="Valuation" value={a.valuation_amount ? `${formatMoney(a.valuation_amount, cur, { decimals: 0 })} · ${formatDate(a.valuation_date)}` : '—'} />
              <KV label="Final offer" value={formatDate(a.final_offer_date)} />
              <KV label="Disbursed" value={a.disbursal_date ? `${formatMoney(a.disbursed_amount, cur, { decimals: 0 })} · ${formatDate(a.disbursal_date)}` : '—'} />
              <KV label="Advisor fee" value={formatMoney(a.advisor_fee_amount, cur)} />
              <KV label="Lender referral fee" value={formatMoney(a.lender_referral_fee_amount, cur)} />
            </MDBox>
          </InfoCard>
        </Grid>
      </Grid>

      <MDBox mt={2}>
        <InfoCard icon="history" title="Stage history">
          {!history.length && <MDTypography variant="button" color="text">No stage changes yet.</MDTypography>}
          {history.map((h) => {
            const t = TONES[toneFor('mortgage', h.to_stage)];
            return (
              <MDBox key={h.id || h.changed_at} display="flex" gap={1.5} mb={1.5}>
                <MDBox sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: t.dot, mt: 0.75, flexShrink: 0 }} />
                <MDBox>
                  <MDTypography variant="button" fontWeight="bold">
                    {h.from_stage ? `${humanize(h.from_stage)} → ${humanize(h.to_stage)}` : humanize(h.to_stage)}
                  </MDTypography>
                  <MDTypography variant="caption" color="text" display="block">
                    {[formatDateTime(h.changed_at), h.changed_by_name].filter(Boolean).join(' · ')}
                  </MDTypography>
                  {h.notes && <MDTypography variant="caption" display="block">{h.notes}</MDTypography>}
                </MDBox>
              </MDBox>
            );
          })}
        </InfoCard>
      </MDBox>

      {a.notes && (
        <MDBox mt={2}>
          <InfoCard icon="sticky_note_2" title="Notes"><MDTypography variant="button">{a.notes}</MDTypography></InfoCard>
        </MDBox>
      )}

      <MortgageForm
        open={editOpen}
        application={a}
        onClose={() => setEditOpen(false)}
        onSaved={(saved) => {
          setEditOpen(false);
          if (saved?.warnings?.length) notify(`Saved with warnings: ${saved.warnings.map((w) => WARNING_TEXT[w] || humanize(w)).join(' ')}`, 'warning');
          else notify('Application saved');
          // Keep the PUT response (it carries `warnings`); history is unchanged by an edit.
          if (saved) setData((cur2) => ({ ...cur2, ...saved, stage_history: saved.stage_history || cur2.stage_history }));
          else reload();
        }}
      />
      <StageDialog app={a} open={stageOpen} onClose={() => setStageOpen(false)} onDone={(s) => { setStageOpen(false); notify(`Moved to ${humanize(s)}`); reload(); }} />
      {snack}
    </RePage>
  );
}
